import { muiPalette } from './muiPalette';
import { muiTypography } from './muiTypography';
import { alertOverride } from './alertOverride';
import { tooltipOverride } from './tooltipOverride';
import { textFieldOverride } from './textFieldOverride';
import { buttonOverride } from './buttonOverride';
import { iconButtonOverride } from './iconButtonOverride';
import { switchOverride } from './switchOverride';
import { chipOverride } from './chipOverride';
import { customShadows } from './shadows';
import { sliderOverride } from './sliderOverride';
import { stepperOverrides } from './stepperOverrides';
import { toggleButtonOverride } from './toggleButtonOverride';
import { tabsOverride, tabOverride } from './tabsOverride';
import { createTheme } from '@mui/material/styles';
import { alertClasses } from '@mui/material/Alert';
const { breakpoints: muiBreakpoints, spacing: muiSpacing } = createTheme();

export const muiOverrides = {
  breakpoints: {
    values: {
      xs: false,
      sm: 0,
      md: 1440,
      lg: 1920,
      xl: false
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: 'inherit'
        },
        body: {
          ...muiTypography.typography.body2
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          hero1: 'h1'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
          '&.formHelperTextWithIcon': {
            display: 'flex',
            alignItems: 'center',
            '& .MuiSvgIcon-root': {
              marginRight: 4
            }
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        padding: {
          paddingTop: 0,
          paddingBottom: 0
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: 4,
          paddingBottom: 4,
          '&.Mui-selected': {
            opacity: 1
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'unset',
          paddingRight: 8
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: 8,
          paddingBottom: 8
        }
      }
    },
    PrivateSwitchBase: {
      styleOverrides: {
        root: {
          padding: '8px'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        indeterminate: {
          color: muiPalette.palette.primary.main
        },
        root: {
          padding: muiSpacing(1),
          '& .MuiSvgIcon-root': {
            fontSize: 19
          },
          '& .MuiSvgIcon-fontSizeSmall': {
            fontSize: 14
          },
          '&.Mui-focusVisible': {
            background: muiPalette.palette.action.focus
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 8,
          '& .MuiSvgIcon-root': {
            fontSize: 16
          },
          '& .MuiSvgIcon-fontSizeSmall': {
            fontSize: 16
          },
          '&.Mui-focusVisible': {
            background: muiPalette.palette.action.focus
          }
        }
      }
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          marginTop: 8
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E5E8ED'
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeSmall: {
          fontSize: 16
        },
        root: ({ ownerState }) => ({
          // on v5 fontSize's style is added to the root
          // we should not override this style if the passed fontSize is 'inherit'
          // otherwise it will be overridden by this root rule
          ...(ownerState.fontSize !== 'inherit' && {
            fontSize: 20
          })
        }),
        fontSizeLarge: {
          fontSize: 24
        }
      }
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& > *': {
            // set styles to a direct child only
            // if we set box-shadow at the root level we will have flickering while transitioning (fading on close and on open).
            boxShadow: customShadows[24]
          },
          [`& .${alertClasses.root}`]: {
            padding: muiSpacing(0, 2)
          },
          [`& .${alertClasses.icon}`]: {
            padding: muiSpacing(1, 0)
          },
          [`& .${alertClasses.action}`]: {
            padding: muiSpacing(0.5, 0, 0.5, 1)
          }
        },
        anchorOriginTopCenter: {
          [muiBreakpoints.up('sm')]: {
            top: 80
          }
        },
        anchorOriginTopRight: {
          [muiBreakpoints.up('sm')]: {
            top: 80
          }
        },
        anchorOriginTopLeft: {
          [muiBreakpoints.up('sm')]: {
            top: 80
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        // on v5 labelPlacement's style is added to the root
        // we should override the style only on default placement which is 'end'
        root: ({ ownerState }) => ({
          ...(ownerState.labelPlacement === 'end' && {
            marginLeft: -10,
            marginRight: 24
          })
        }),
        label: {
          ...muiTypography.typography.body2
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: muiPalette.palette.text.primary,
          '&.Mui-focused': {
            color: muiPalette.palette.text.primary
          }
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          marginRight: 4,
          marginLeft: 4
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // card should have 24px padding
          // car header and card content already got 16px.
          padding: 8
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: muiPalette.palette.action.backdrop
          }
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: muiSpacing(0, 3, 3, 3)
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          paddingTop: muiSpacing(0.5),
          paddingBottom: muiSpacing(0.5)
        }
      }
    },
    MuiStaticDatePicker: {
      defaultProps: {
        slotProps: {
          nextIconButton: {
            'data-qa-id': 'date-picker__nextButton'
          },
          previousIconButton: {
            'data-qa-id': 'date-picker__previousButton'
          }
        }
      }
    },
    MuiStaticDateRangePicker: {
      defaultProps: {
        slotProps: {
          nextIconButton: {
            'data-qa-id': 'date-range-picker__nextButton'
          },
          previousIconButton: {
            'data-qa-id': 'date-range-picker__previousButton'
          }
        }
      }
    },
    ...toggleButtonOverride,
    ...tabsOverride,
    ...tabOverride,
    ...alertOverride,
    ...chipOverride,
    ...buttonOverride,
    ...iconButtonOverride,
    ...switchOverride,
    ...sliderOverride.theme,
    ...stepperOverrides,
    ...tooltipOverride,
    ...textFieldOverride
  }
};
